import { useEffect, useRef, useState } from "react";
import Icon from "../Icon";
import DatePicker from "../forms/DatePicker";
import { usePosicionDesplegable } from "../posicionDesplegable";

// Un solo control «Fechas» para el Cuaderno. Antes eran cuatro piezas sueltas en la
// barra de filtros (periodo, desde, hasta y mes) y bastaba con una para filtrar, asi
// que nadie sabia cual mandaba. Aqui van juntas en un desplegable y el boton muestra
// el filtro que esta activo, con una x para quitarlo.
//
// Los cuatro valores (per, mes, desde, hasta) y su exclusion mutua son los de siempre:
// elegir uno vacia los demas, igual que hacian los controles sueltos. Lo que cambia es
// DONDE se eligen, no lo que filtran.
export const PERIODOS = [
  ["", "Todo el período"], ["hoy", "Hoy"], ["ultimo_dia", "Último día"],
  ["semana", "Semana actual"], ["semana_anterior", "Semana anterior"],
];

const dmy = iso => { const [y, m, d] = String(iso).slice(0, 10).split("-"); return `${d}/${m}/${y}`; };
export const etiquetaMes = m =>
  new Date(m + "-02").toLocaleString("es-ES", { month: "long", year: "numeric" }).replace(/^\w/, c => c.toUpperCase());

// El texto del filtro activo, o null si no hay ninguno que acote.
export function etiquetaFechas({ per, mes, desde, hasta }) {
  if (desde && hasta) return desde === hasta ? dmy(desde) : `${dmy(desde)} – ${dmy(hasta)}`;
  if (desde) return `Desde ${dmy(desde)}`;
  if (hasta) return `Hasta ${dmy(hasta)}`;
  if (mes) return etiquetaMes(mes);
  const p = PERIODOS.find(([k]) => k === per);
  return p && p[0] ? p[1] : null;
}

export default function FiltroFechas({ per, mes, desde, hasta, meses = [], onChange }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  const boton = useRef(null);
  const panelRef = useRef(null);
  const { estilo: posicion } = usePosicionDesplegable(abierto, ref, panelRef, { separacion: 6 });
  const activo = etiquetaFechas({ per, mes, desde, hasta });

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    const tecla = e => { if (e.key === "Escape") { setAbierto(false); boton.current?.focus(); } };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", tecla); };
  }, [abierto]);

  const vacio = { per: "", mes: "", desde: "", hasta: "" };
  // Elegir una forma de filtrar vacia las otras tres
  const poner = patch => onChange({ ...vacio, ...patch });

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <button ref={boton} type="button" className="btn" aria-expanded={abierto} aria-haspopup="dialog"
        aria-label={activo ? `Fechas: ${activo}` : "Fechas"}
        onClick={() => setAbierto(a => !a)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: activo ? "5px 26px 5px 9px" : "5px 9px",
          borderRadius: 7, cursor: "pointer", fontSize: 11.5, fontFamily: "var(--font)", whiteSpace: "nowrap",
          border: `1px solid ${activo ? "var(--accent)" : "var(--border-ctl)"}`,
          background: activo ? "var(--accent-bg)" : "var(--surface2)",
          color: activo ? "var(--accent)" : "var(--text2)", fontWeight: activo ? 700 : 400 }}>
        <Icon name="calendar3" size={11} color={activo ? "var(--accent)" : "var(--text3)"} />
        {activo ? <><span style={{ fontWeight: 400, opacity: .8 }}>Fechas:</span> {activo}</> : "Fechas"}
        {!activo && <Icon name="chevron-down" size={10} color="var(--text3)" />}
      </button>
      {activo && (
        <button type="button" onClick={() => poner({})} title="Quitar el filtro de fechas"
          aria-label="Quitar el filtro de fechas"
          style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", background: "none",
            border: "none", cursor: "pointer", fontSize: 14, lineHeight: 1, color: "var(--accent)", padding: "2px 5px",
            borderRadius: 5, fontFamily: "var(--font)" }}>×</button>
      )}

      {abierto && (
        <div ref={panelRef} role="dialog" aria-label="Filtrar por fechas"
          style={{ zIndex: 500, minWidth: 250,
            background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12,
            boxShadow: "var(--shadow-lg)", padding: 12, display: "flex", flexDirection: "column", gap: 10, ...posicion,
            // Sin tope ni scroll propios: dentro van los selectores de fecha, cuyo calendario se
            // desplegaria recortado por un contenedor con overflow.
            overflowY: "visible", maxHeight: "none" }}>
          <div role="group" aria-label="Período" style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {PERIODOS.map(([k, lb]) => {
              const on = !desde && !hasta && !mes && per === k;
              return (
                <button key={k || "todo"} type="button" className="btn" aria-pressed={on}
                  onClick={() => { poner({ per: k }); setAbierto(false); }}
                  style={{ padding: "4px 9px", borderRadius: 20, cursor: "pointer", fontSize: 11, fontFamily: "var(--font)",
                    border: `1px solid ${on ? "var(--accent)" : "var(--border-ctl)"}`,
                    background: on ? "var(--accent-bg)" : "var(--surface)",
                    color: on ? "var(--accent)" : "var(--text2)", fontWeight: on ? 700 : 500 }}>
                  {lb}
                </button>
              );
            })}
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 10, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: ".07em", color: "var(--text3)" }}>
            Mes
            <select value={mes} onChange={e => poner({ mes: e.target.value })}
              style={{ padding: "5px 8px", borderRadius: 7, border: "1px solid var(--border-ctl)", fontSize: 11.5,
                color: "var(--text2)", background: "var(--surface2)", fontFamily: "var(--font)", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
              <option value="">Ningún mes en concreto</option>
              {meses.map(m => <option key={m} value={m}>{etiquetaMes(m)}</option>)}
            </select>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {[["Desde", desde, v => poner({ desde: v, hasta })], ["Hasta", hasta, v => poner({ desde, hasta: v })]].map(([lb, valor, fn]) => (
              <div key={lb} style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em",
                  color: "var(--text3)", marginBottom: 3 }}>{lb}</div>
                <DatePicker compact nombre={lb} value={valor} placeholder="—" onChange={fn} margenInferior={0} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
